import {
  Candidate,
  Command,
  dayMonthDateString,
  InterestedUser,
  Shift,
  shiftTimeRangeDurationString,
  UserRole,
} from '@nextforce/api'
import { Divider, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import React, { useCallback, useState } from 'react'
import { View } from 'react-native'
import { CommandButtons } from 'shared/components/command_buttons'
import { UserDetails } from 'shared/components/user/user_details'
import { CandidateNote } from './helper_shift_details'

interface Props {
  shift: Shift
  onButtonPress(command: Command, note?: string): Promise<void>
  interests?: number
  candidates?: Candidate[]
  role?: UserRole
}

export function ManagerShiftDetails({
  shift,
  onButtonPress,
  interests,
  candidates,
  role,
}: Props) {
  const isNoInterestOpenShift = shift.open && !interests && !candidates
  const styles = useStyles(isNoInterestOpenShift)

  const showCandidateNoteInput =
    role === 'manager' &&
    shift.commands?.some(
      command =>
        command.type === 'action' &&
        (command.name === 'crew_swap_shift' || command.name === 'request_cover')
    )

  const [note, setNote] = useState<string | undefined>()

  const onPress = useCallback(
    async (command: Command) => {
      await onButtonPress(command, note)
    },
    [note, onButtonPress]
  )

  return (
    <View style={styles.container}>
      <TimingDetails shift={shift} />
      {interests && candidates ? (
        <View>
          <Text style={styles.availableCrewText}>{t('crewAvailable')}</Text>
          <InterestedCrewList candidates={candidates} onButtonPress={onPress} />
        </View>
      ) : !shift.open ? (
        <UserDetails user={shift.user} />
      ) : null}
      {showCandidateNoteInput ? (
        <CandidateNote note={note} setNote={setNote} />
      ) : null}
      <CommandButtons commands={shift.commands} onButtonPress={onPress} />
    </View>
  )
}

interface TimingDetailsProps {
  shift: Shift
}
function TimingDetails(props: TimingDetailsProps) {
  const styles = useStyles(props.shift.bonus?.isModified)
  return (
    <View style={styles.card}>
      <Text style={styles.date}>{dayMonthDateString(props.shift.date)}</Text>
      <Text style={styles.time}>
        {shiftTimeRangeDurationString(props.shift)}
      </Text>
      <Text style={styles.shiftPosition}>
        {props.shift.position ?? t('anyPosition')}
      </Text>
      {props.shift.managerProxyAssignmentMessage ? (
        <>
          <Divider />
          <Text style={styles.proxyText}>
            {props.shift.managerProxyAssignmentMessage}
          </Text>
        </>
      ) : null}
      {props.shift.bonus && (
        <Text style={styles.bonus}>{`$ ${props.shift.bonus?.amount}`}</Text>
      )}
    </View>
  )
}

interface InterestedCrewListProps {
  candidates?: Candidate[] | InterestedUser[]
  onButtonPress(command: Command): Promise<void>
}

export function InterestedCrewList({
  candidates,
  onButtonPress,
}: InterestedCrewListProps) {
  if (!candidates) {
    return null
  }

  return (
    <>
      <View>
        {candidates.map(candidate => (
          <UserDetails
            key={String(candidate.user?.id)}
            user={candidate.user}
            isInterested={true}
            command={candidate.commands[0]}
            onButtonPress={onButtonPress}
            candidateNote={candidate.candidateNote}
          />
        ))}
      </View>
    </>
  )
}

const useStyles = makeStyles((theme, isModified: boolean) => ({
  container: {
    flex: 1,
    justifyContent: 'flex-start',
    backgroundColor: theme.colors?.grey5,
  },
  card: {
    justifyContent: 'center',
    backgroundColor: theme.colors?.white,
    shadowColor: '#000000',
    shadowOffset: {
      width: 0,
      height: 3,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
    marginBottom: 17,
  },
  date: {
    fontSize: 19,
    fontFamily: font.bold,
    margin: 10,
    marginBottom: 5,
  },
  time: {
    fontSize: 16,
    marginLeft: 10,
    color: theme.colors?.grey1,
  },
  shiftPosition: {
    fontSize: 17,
    margin: 10,
  },
  bonus: {
    fontSize: 17,
    marginLeft: 10,
    marginBottom: 10,
    color: isModified === true ? theme.colors?.primary : theme.colors?.black,
  },
  name: {
    margin: 10,
    fontFamily: font.bold,
    fontSize: 19,
  },
  availableCrewText: {
    margin: 10,
    fontSize: 19,
  },
  proxyText: {
    color: theme.colors.grey1,
    margin: 10,
    paddingLeft: 5,
    fontSize: 16,
  },
}))
